import React, { useEffect } from 'react';
import { X, Eye, Calendar, User, Mail } from 'lucide-react';

interface CategoryStatsModalProps {
   isOpen: boolean;
   onClose: () => void;
   category: 'albums' | 'events' | 'trips' | 'total' | null;
   selectedStatus: 'accepted' | 'pending' | 'rejected' | 'invited' | 'total' | null;
   invitations: any[];
   loading: boolean;
   onInvitationClick: (invitationId: string, type: 'album' | 'event' | 'trip') => void;
}

const CategoryStatsModal: React.FC<CategoryStatsModalProps> = ({
   isOpen,
   onClose,
   category,
   selectedStatus,
   invitations,
   loading,
   onInvitationClick,
}) => {
   // Debug logging
   console.log('🎭 CategoryStatsModal props:', {
      isOpen,
      category,
      selectedStatus,
      invitationsCount: invitations.length,
      loading,
   });

   // Handle escape key press
   useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
         if (e.key === 'Escape') {
            onClose();
         }
      };

      if (isOpen) {
         document.addEventListener('keydown', handleEscape);
         document.body.style.overflow = 'hidden';
      }

      return () => {
         document.removeEventListener('keydown', handleEscape);
         document.body.style.overflow = 'unset';
      };
   }, [isOpen, onClose]);

   const getStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
         case 'accepted':
            return 'bg-green-100 text-green-800';
         case 'pending':
            return 'bg-yellow-100 text-yellow-800';
         case 'rejected':
            return 'bg-red-100 text-red-800';
         case 'invited':
            return 'bg-blue-100 text-blue-800';
         default:
            return 'bg-gray-100 text-gray-800';
      }
   };

   const getTypeIcon = (type: string) => {
      switch (type) {
         case 'album':
            return '📸';
         case 'event':
            return '🎉';
         case 'trip':
            return '✈️';
         default:
            return '📝';
      }
   };

   const getCategoryIcon = (cat: string) => {
      switch (cat) {
         case 'albums':
            return '📸';
         case 'events':
            return '🎉';
         case 'trips':
            return '✈️';
         case 'total':
            return '📊';
         default:
            return '📊';
      }
   };

   const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   if (!isOpen || !category) return null;

   const categoryTitle = category.charAt(0).toUpperCase() + category.slice(1);
   const statusTitle =
      selectedStatus && selectedStatus !== 'total'
         ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)
         : 'All';

   const modalTitle =
      selectedStatus && selectedStatus !== 'total'
         ? `${statusTitle} ${categoryTitle} Invitations`
         : `${categoryTitle} Invitations Details`;

   const modalSubtitle =
      selectedStatus && selectedStatus !== 'total'
         ? `Showing only ${selectedStatus} invitations for ${categoryTitle.toLowerCase()}`
         : `Detailed breakdown of ${categoryTitle.toLowerCase()} invitations`;

   // Calculate stats for the filtered invitations
   const stats = {
      total: invitations.length,
      accepted: invitations.filter((inv) => inv.status === 'accepted').length,
      pending: invitations.filter((inv) => inv.status === 'pending').length,
      rejected: invitations.filter((inv) => inv.status === 'rejected').length,
      invited: invitations.filter((inv) => inv.status === 'invited').length,
   };

   const acceptanceRate =
      stats.total > 0 ? ((stats.accepted / stats.total) * 100).toFixed(1) : '0.0';

   return (
      <div className='fixed inset-0 z-[9999] overflow-y-auto'>
         {/* Backdrop */}
         <div
            className='fixed inset-0 bg-black bg-opacity-50 transition-opacity'
            onClick={onClose}
         />

         {/* Modal */}
         <div className='flex min-h-full items-center justify-center p-4'>
            <div className='relative w-full max-w-6xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all'>
               {/* Header */}
               <div className='flex items-center justify-between p-6 border-b border-gray-200'>
                  <div className='flex items-center space-x-3'>
                     <span className='text-2xl'>{getCategoryIcon(category)}</span>
                     <div>
                        <h3 className='text-lg font-medium leading-6 text-gray-900'>
                           {modalTitle}
                        </h3>
                        <p className='text-sm text-gray-500'>{modalSubtitle}</p>
                        {selectedStatus && selectedStatus !== 'total' && (
                           <div className='mt-2'>
                              <span
                                 className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    selectedStatus === 'accepted'
                                       ? 'bg-green-100 text-green-800'
                                       : selectedStatus === 'pending'
                                       ? 'bg-yellow-100 text-yellow-800'
                                       : selectedStatus === 'rejected'
                                       ? 'bg-red-100 text-red-800'
                                       : selectedStatus === 'invited'
                                       ? 'bg-blue-100 text-blue-800'
                                       : 'bg-gray-100 text-gray-800'
                                 }`}
                              >
                                 Filtered by: {statusTitle}
                              </span>
                           </div>
                        )}
                     </div>
                  </div>
                  <button
                     type='button'
                     className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                     onClick={onClose}
                  >
                     <span className='sr-only'>Close</span>
                     <X className='h-6 w-6' />
                  </button>
               </div>

               {/* Content */}
               <div className='p-6 max-h-[80vh] overflow-y-auto'>
                  {loading ? (
                     <div className='flex items-center justify-center h-64'>
                        <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600'></div>
                     </div>
                  ) : (
                     <>
                        {/* Summary Stats */}
                        <div className='grid grid-cols-2 md:grid-cols-5 gap-4 mb-6'>
                           {[
                              { label: 'Total', value: stats.total, color: 'bg-blue-500' },
                              { label: 'Accepted', value: stats.accepted, color: 'bg-green-500' },
                              { label: 'Pending', value: stats.pending, color: 'bg-yellow-500' },
                              { label: 'Rejected', value: stats.rejected, color: 'bg-red-500' },
                              { label: 'Invited', value: stats.invited, color: 'bg-purple-500' },
                           ].map((stat) => (
                              <div key={stat.label} className='bg-gray-50 p-4 rounded-lg'>
                                 <div className='flex items-center justify-between'>
                                    <div>
                                       <p className='text-sm font-medium text-gray-600'>
                                          {stat.label}
                                       </p>
                                       <p className='text-2xl font-bold text-gray-900'>
                                          {stat.value}
                                       </p>
                                    </div>
                                    <div
                                       className={`w-8 h-8 ${stat.color} rounded-full flex items-center justify-center`}
                                    >
                                       <span className='text-white text-xs font-bold'>
                                          {stat.value}
                                       </span>
                                    </div>
                                 </div>
                              </div>
                           ))}
                        </div>

                        {/* Acceptance Rate */}
                        <div className='bg-blue-50 p-4 rounded-lg mb-6'>
                           <div className='flex items-center justify-between'>
                              <div>
                                 <h4 className='text-lg font-medium text-blue-900'>
                                    Acceptance Rate
                                 </h4>
                                 <p className='text-sm text-blue-700'>
                                    Overall success rate for {categoryTitle.toLowerCase()}{' '}
                                    invitations
                                 </p>
                              </div>
                              <div className='text-3xl font-bold text-blue-900'>
                                 {acceptanceRate}%
                              </div>
                           </div>
                        </div>

                        {/* Invitations List */}
                        <div className='bg-white border border-gray-200 rounded-lg'>
                           <div className='px-6 py-4 border-b border-gray-200'>
                              <h4 className='text-lg font-medium text-gray-900'>
                                 {selectedStatus && selectedStatus !== 'total'
                                    ? `${statusTitle} ${categoryTitle} Invitations (${invitations.length})`
                                    : `${categoryTitle} Invitations (${invitations.length})`}
                              </h4>
                              <p className='text-sm text-gray-500'>
                                 Click on any invitation to view detailed information
                              </p>
                           </div>

                           {invitations.length > 0 ? (
                              <ul className='divide-y divide-gray-200'>
                                 {invitations.map((invitation) => (
                                    <li
                                       key={invitation.id}
                                       className='px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors'
                                       onClick={() =>
                                          onInvitationClick(invitation.id, invitation.type)
                                       }
                                    >
                                       <div className='flex items-center justify-between'>
                                          <div className='flex items-center space-x-4'>
                                             <div className='flex-shrink-0'>
                                                <span className='text-2xl'>
                                                   {getTypeIcon(invitation.type)}
                                                </span>
                                             </div>
                                             <div className='flex-1 min-w-0'>
                                                <div className='flex items-center space-x-3'>
                                                   <h5 className='text-sm font-medium text-gray-900 truncate'>
                                                      {invitation.type.charAt(0).toUpperCase() +
                                                         invitation.type.slice(1)}{' '}
                                                      Invitation
                                                   </h5>
                                                   <span
                                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                                         invitation.status
                                                      )}`}
                                                   >
                                                      {invitation.status.charAt(0).toUpperCase() +
                                                         invitation.status.slice(1)}
                                                   </span>
                                                </div>
                                                <div className='mt-1 flex items-center space-x-4 text-sm text-gray-500'>
                                                   <div className='flex items-center space-x-1'>
                                                      <User className='h-4 w-4' />
                                                      <span>From: {invitation.inviterName}</span>
                                                   </div>
                                                   <div className='flex items-center space-x-1'>
                                                      <Mail className='h-4 w-4' />
                                                      <span>{invitation.recipient_email}</span>
                                                   </div>
                                                   <div className='flex items-center space-x-1'>
                                                      <Calendar className='h-4 w-4' />
                                                      <span>
                                                         {formatDate(invitation.createdAt)}
                                                      </span>
                                                   </div>
                                                </div>
                                                {invitation.eventTitle && (
                                                   <p className='mt-1 text-sm text-gray-600'>
                                                      Event: {invitation.eventTitle}
                                                   </p>
                                                )}
                                                {invitation.customMessage && (
                                                   <p className='mt-1 text-sm text-gray-600'>
                                                      Message: {invitation.customMessage}
                                                   </p>
                                                )}
                                             </div>
                                          </div>
                                          <div className='flex items-center space-x-2'>
                                             <Eye className='h-5 w-5 text-gray-400' />
                                             <span className='text-sm text-gray-500'>
                                                View Details
                                             </span>
                                          </div>
                                       </div>
                                    </li>
                                 ))}
                              </ul>
                           ) : (
                              <div className='px-6 py-12 text-center'>
                                 <p className='text-gray-500'>
                                    No {categoryTitle.toLowerCase()} invitations found
                                 </p>
                              </div>
                           )}
                        </div>
                     </>
                  )}
               </div>

               {/* Footer */}
               <div className='bg-gray-50 px-6 py-3'>
                  <div className='flex justify-end'>
                     <button
                        type='button'
                        className='inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        onClick={onClose}
                     >
                        Close
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default CategoryStatsModal;
